import React, { useState, useRef } from "react";
import ReactDOM from "react-dom/client";
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { useMobile } from "@app/scripts/react-hooks/mobile";

type VideoSource = {
  mime_type: string;
  url: string;
};

type Props = {
  srcSet: string | null;
  src: string | VideoSource[];
  largeSrc: string;
  sizes: string | null;
  alt: string | null;
  mediaType: "image" | "video";
  autoplay: 'true' | 'false';
};

const Zoomable: React.FC<Props> = ({ largeSrc, alt = "", srcSet = "", sizes = "", mediaType = "image", src, autoplay }) => {
  const autoplayVideo = autoplay !== 'false'
  
  const transformComponentRef = useRef<ReactZoomPanPinchRef>(null);
  const imageSlideRef = useRef<HTMLImageElement>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [initialScale, setInitialScale] = useState(1);
  
  const isKid = document.querySelector(".is-industrie-kids") ? true : false;
  const isMobile = useMobile();
  const parent = imageSlideRef.current?.closest('[id^="item-carousel-"]');

  const zoomIn = () => {
    const step = isKid && !isMobile ? 1 : 0.5;
    transformComponentRef.current?.zoomIn(step);
  };

  const zoomOut = () => {
    const step = isKid && !isMobile ? 1 : 0.5;
    transformComponentRef.current?.zoomOut(step);
  };

  const toggleZoom = () => {
    if (isDisabled) {
      zoomIn();
      setIsDisabled(false);
      if (parent) {
        parent.querySelector(".product-grid")?.classList.add("swiper-no-swiping");
      }
    } else {
      zoomOut();
      setIsDisabled(true);
      setInitialScale(1);
      if (parent) {
        parent.querySelector(".product-grid")?.classList.remove("swiper-no-swiping");
      }
    }
  };

  const Controls = () => {
    return (
      <div className="product__zoom-controls">
        <button className={`zoom-out ${!isDisabled ? "hidden" : ""}`} onClick={toggleZoom} aria-label="Zoom image out">
          <ui-icon width="16" height="16" name="icon-zoom-in"></ui-icon>
        </button>
        <button className={`zoom-in ${isDisabled ? "hidden" : ""}`} onClick={toggleZoom} aria-label="Zoom image in">
          <ui-icon width="16" height="16" name="icon-close-large"></ui-icon>
        </button>
      </div>
    );
  };

  return (
    <div className="h-full">
      {mediaType === "image" ? (
        <TransformWrapper
          initialScale={initialScale}
          ref={transformComponentRef}
          disabled={isDisabled}
          centerOnInit={true}
          doubleClick={{ disabled: (isKid ? false : isMobile) }}
          zoomAnimation={{ disabled: true }}
        >
          {() => (
            <>
              <TransformComponent wrapperClass="h-full" contentClass="h-full">
                <img
                  ref={imageSlideRef}
                  src={largeSrc}
                  alt={alt || ""}
                  srcSet={srcSet || ""}
                  sizes={sizes || ""}
                  className={`!pointer-events-auto object-cover ${isDisabled ? "cursor-zoom-in" : "cursor-zoom-out"}`}
                  onClick={isKid ? toggleZoom : !isMobile ? toggleZoom : undefined}
                  loading="eager"
                />
              </TransformComponent>
              <Controls />
            </>
          )}
        </TransformWrapper>
      ) : (
        <video playsInline={autoplayVideo} muted={autoplayVideo} loop={autoplayVideo} autoPlay={autoplayVideo} controls={!autoplayVideo}>
          {typeof src !== "string" &&
            src.map((vidSource) => <source src={vidSource.url} type={vidSource.mime_type}></source>)}
        </video>
      )}
    </div>
  );
};

class ZoomableElem extends HTMLElement {
  static observedAttributes = ["srcset", "src", "large-src", "sizes", "alt", "autoplay"];

  connectedCallback() {
    const props = {
      srcSet: this.getAttribute("srcset"),
      src: this.getAttribute("src"),
      largeSrc: this.getAttribute("large-src"),
      sizes: this.getAttribute("sizes"),
      alt: this.getAttribute("alt"),
      mediaType: this.getAttribute("media-type"),
      autoplay: this.getAttribute("autoplay"),
    };

    if (!props.src || !props.largeSrc) {
      console.error("srcs not provided.");
      return;
    }

    if (props.mediaType === "video") {
      props.src = JSON.parse(props.src);
    }

    const mountPoint = document.createElement("div");

    // set min height to original image to prevent flash
    mountPoint.style.minHeight = `${this.querySelector(".image-container")?.getBoundingClientRect()?.height}px`;

    this.innerHTML = "";
    this.appendChild(mountPoint);

    const root = ReactDOM.createRoot(mountPoint);
    root.render(<Zoomable {...props} />);

    // remove height afterwards
    setTimeout(() => (mountPoint.style.minHeight = ""), 200);
  }
}

customElements.define("ui-zoomable", ZoomableElem);
